'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Command } from 'cmdk';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Folder, Terminal, Box, Home, Activity, Layers, Download, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { ProjectService } from '../lib/ProjectService';
import { ProjectHistoryItem } from '../lib/types';

export default function CommandPalette() {
    const [open, setOpen] = useState(false);
    const [projects, setProjects] = useState<ProjectHistoryItem[]>([]);
    const router = useRouter();

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((open) => !open);
            }
        };

        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, []);

    useEffect(() => {
        if (open) {
            ProjectService.list().then((res) => {
                setProjects(res.projects || []);
            }).catch(console.error);
        }
    }, [open]);

    const runCommand = useCallback((command: () => void) => {
        setOpen(false);
        command();
    }, []);

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-slate-900 dark:bg-slate-100 dark:bg-slate-800 dark:text-slate-900 dark:text-white/40 backdrop-blur-md"
                        onClick={() => setOpen(false)}
                    />
                    
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -20 }}
                        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                        className="relative w-full max-w-2xl bg-white dark:bg-slate-900 dark:text-white rounded-[2.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border border-slate-100 dark:border-slate-800 overflow-hidden"
                    >
                        <Command className="flex flex-col h-full">
                            <div className="flex items-center px-6 border-b border-slate-100 dark:border-slate-800">
                                <Search className="text-slate-400 dark:text-slate-500 mr-4" size={20} strokeWidth={2.5} />
                                <Command.Input 
                                    placeholder="Search or execute command..." 
                                    className="w-full h-16 bg-transparent border-none outline-none text-slate-900 dark:text-white font-bold placeholder:text-slate-300 placeholder:font-black placeholder:uppercase placeholder:tracking-widest text-sm"
                                />
                                <div className="px-2 py-1 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 rounded-lg text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">ESC</div>
                            </div>

                            <Command.List className="max-h-[400px] overflow-y-auto p-4 custom-scrollbar-light">
                                <Command.Empty className="py-12 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 gap-3">
                                    <Box size={32} className="opacity-20" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">No matching commands found</span>
                                </Command.Empty>

                                <Command.Group heading={<span className="px-3 text-[9px] font-black text-slate-300 uppercase tracking-[0.2em] mb-2 block">Quick Actions</span>}>
                                    <Item onSelect={() => runCommand(() => router.push('/' as any))}>
                                        <Home size={18} />
                                        <span>Return to Dashboard</span>
                                        <Shortcut>⌘H</Shortcut>
                                    </Item>
                                    <Item onSelect={() => runCommand(() => router.push('/project/new' as any))}>
                                        <Activity size={18} />
                                        <span>Initialize New Project</span>
                                        <Shortcut>⌘N</Shortcut>
                                    </Item>
                                </Command.Group>

                                {projects.length > 0 && (
                                    <Command.Group heading={<span className="px-3 text-[9px] font-black text-slate-300 uppercase tracking-[0.2em] my-4 block">Active Workspaces</span>}>
                                        {projects.map((p) => (
                                            <Item key={p.id} onSelect={() => runCommand(() => router.push(`/project/${p.id}` as any))}>
                                                <Folder size={18} />
                                                <div className="flex flex-col">
                                                    <span className="font-bold">{p.use_case}</span>
                                                    <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none mt-1">{p.id.substring(0, 8)} • {new Date(p.created_at).toLocaleDateString()}</span>
                                                </div>
                                            </Item>
                                        ))}
                                    </Command.Group>
                                )}

                                <Command.Group heading={<span className="px-3 text-[9px] font-black text-slate-300 uppercase tracking-[0.2em] my-4 block">System Control</span>}>
                                    <Item onSelect={() => runCommand(() => window.location.reload())}>
                                        <Layers size={18} />
                                        <span>Force Re-sync State</span>
                                    </Item>
                                    <Item onSelect={() => runCommand(() => setOpen(false))}>
                                        <X size={18} />
                                        <span>Close</span>
                                    </Item>
                                </Command.Group>
                            </Command.List>

                            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1.5">
                                        <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-md text-[9px] font-black shadow-sm">↑↓</kbd>
                                        <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Navigate</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-md text-[9px] font-black shadow-sm">ENTER</kbd>
                                        <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Execute</span>
                                    </div>
                                </div>
                                <span className="text-[9px] font-black text-blue-500 uppercase tracking-widest">VET Engine α</span>
                            </div>
                        </Command>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

function Item({ children, onSelect }: { children: React.ReactNode; onSelect: () => void }) {
    return (
        <Command.Item
            onSelect={onSelect}
            className="flex items-center gap-4 px-4 py-3 rounded-2xl text-sm font-bold text-slate-600 dark:text-slate-400 dark:text-slate-500 cursor-pointer transition-all duration-300 aria-selected:bg-blue-50 dark:bg-blue-900/30 aria-selected:text-blue-600 dark:text-blue-400 aria-selected:scale-[1.02] group"
        >
            {children}
        </Command.Item>
    );
}

function Shortcut({ children }: { children: React.ReactNode }) {
    return (
        <div className="ml-auto flex items-center gap-1">
            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest group-aria-selected:text-blue-400">
                {children}
            </span>
        </div>
    );
}
